import { Component, computed, effect, inject, input, linkedSignal, signal } from '@angular/core';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { HlmCardImports } from '@spartan-ng/helm/card';

import { Cartographie } from '../cartographie/models/cartographie.model';
import { Étape } from '../référentiel-métier-parcours/models/étape.model';
import { ParcoursStore } from '../référentiel-métier-parcours/store/parcours.store';
import { ExpansionPanel } from '../shared/components/expansion-panel/expansion-panel.component';
import { SERVICE_INSTITUTS_METIER } from '../shared/models/service-instituts-métier';
import { TrierParPipe } from '../shared/pipes/trier-par.pipe';
import { CardEtapeComponent } from './card-etape/card-etape.component';
import { HeaderCatalogueNationalComponent } from './header-catalogue-national/header-catalogue-national.component';
import { REFERENTIEL_TYPE } from '../administration/gestion-référentiels/models/référentiel-type.enum';
import { Référentiel } from '../administration/gestion-référentiels/models/référentiel.model';
import { ReferentielStore } from '../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-catalogue-national',
  imports: [
    ExpansionPanel,
    CardEtapeComponent,
    TrierParPipe,
    HeaderCatalogueNationalComponent,
    HlmCardImports,
    EmptyStateComponent,
  ],
  templateUrl: './catalogue-national.component.html',
})
export class CatalogueNationalComponent {
  readonly #referentielStore = inject(ReferentielStore);
  readonly #parcoursStore = inject(ParcoursStore);

  readonly cartographie = input.required<Cartographie>();

  readonly institut = input<string>();
  readonly metier = input<string>();
  readonly publicsCibles = input<string[]>();
  readonly filtres = computed(() => ({
    metier: this.metier(),
    institut: this.institut(),
    publicsCibles: this.publicsCibles(),
  }));

  readonly etapes = linkedSignal<Étape[]>(() =>
    this.#parcoursStore.entities().flatMap((x) => x.noeuds.filter((x) => !x.estPointDepart)),
  );
  readonly data = signal<
    {
      equipeResponsable: { href: string; nom: string; type: REFERENTIEL_TYPE };
      referentiels: (Référentiel & { etapes: Étape[] })[];
    }[]
  >([]);
  readonly filtreFormations = signal<string>('');

  readonly equipesResponsables = SERVICE_INSTITUTS_METIER.flatMap((x) => x.items);

  constructor() {
    effect(() => {
      this.#referentielStore.chargerParCartographie(this.cartographie().id);
      this.#parcoursStore.charger({ idCartographie: this.cartographie().id });
    });

    effect(() => {
      const filtres = this.filtres();
      const étapes = this.etapes();

      const idRéférentiel = filtres.metier;

      const result: {
        equipeResponsable: { href: string; nom: string; type: REFERENTIEL_TYPE };
        referentiels: (Référentiel & { etapes: Étape[] })[];
      }[] = this.equipesResponsables
        .filter((x) => (filtres.institut ? x.href === filtres.institut : true))
        .map((er) => {
          const filteredRéférentiels = this.#referentielStore
            .referentielsActifs()
            .filter((x) => x.equipeResponsable === er.href)
            .filter((x) => (idRéférentiel ? x.id === idRéférentiel : true))
            .map((référentiel) => {
              let filteredÉtapes = étapes.filter((x) => x.idReferentiel === référentiel.id);

              if (filtres.publicsCibles && filtres.publicsCibles.length > 0) {
                filteredÉtapes = filteredÉtapes.filter((x) =>
                  filtres.publicsCibles?.some((y) => x.publicsCibles?.includes(y)),
                );
              }

              return {
                ...référentiel,
                etapes: filteredÉtapes,
              };
            })
            .filter((x) => x !== null)
            .filter((x) => (this.filtreFormations() ? x.etapes.length : true));

          return {
            equipeResponsable: er,
            referentiels: filteredRéférentiels,
          };
        })
        .filter((x) => x !== null)
        .filter((x) => (this.filtreFormations() ? x.referentiels.flatMap((x) => x.etapes).length : true));

      this.data.set(result);
    });
  }

  getTitre = (equipeResponsable: { href: string; nom: string; type: REFERENTIEL_TYPE }) =>
    (equipeResponsable.type === REFERENTIEL_TYPE.METIER ? equipeResponsable.href + ' - ' : '') + equipeResponsable.nom;

  filtrerFormations(recherche: string) {
    this.filtreFormations.set(recherche);
    const etapes = this.#parcoursStore
      .entities()
      .flatMap((x) => x.noeuds)
      .filter((x) => !x.estPointDepart && x.nom?.toLowerCase().includes(recherche.toLowerCase()));

    this.etapes.set(etapes);
  }
}

export interface FiltresCatalogueNational {
  institut?: string;
  metier?: string;
  publicsCibles?: string[];
}
